import jwt_decode from "jwt-decode";
import { takeLatest, put, call, delay } from "redux-saga/effects";
import { Creators as CreatorsUserSession } from "store/user-session/user-session-duck";
import { IIdentityTokenResponse, ITokenResponse } from "store/user-session/user-session-model";
import { clearLocalStorage, saveNotificationToLocalStorage } from "utils/auth/localStorage";
import TokenService from "utils/auth/Token.service";
import { isUnknown, setCookiesCurrentPage } from "utils/functions";
import { navigateTo } from "utils/functions/navigationUtils";
import YmsNotification from "utils/functions/notification";
import { translate } from "utils/intl/IntlProvider";
import { getError } from "utils/requests/api.errors";
import * as api from "./multiple-realms-api";
import { Types, Creators } from "./multiple-realms-duck";

export const getState = (state) => state.MultipleRealms;

export const getMissingTokenRedirect = (firstAccess?: boolean) => (firstAccess ? "/multiple-realms?error=unregistered" : "/multiple-realms?error=inactive");

const tokenService = new TokenService();

export function handleInvalidToken() {
  tokenService.deleteToken();
  clearLocalStorage();
}

export function* getTokenAsync(action) {
  try {
    yield put(Creators.multipleRealmsShowLoading());
    if (!action.authorizationCode) {
      throw new Error("AuthorizationCode not informed");
    }
    const response = yield call(api.getToken, { code: action.authorizationCode });

    const identityTokenResponse: IIdentityTokenResponse = response?.data;
    const token = identityTokenResponse?.token;
    if (!token) {
      navigateTo(getMissingTokenRedirect(response?.data?.firstAccess));
      tokenService.deleteToken();
      clearLocalStorage();
      return;
    }
    const tokenDecoded: ITokenResponse = jwt_decode(token);
    if (isUnknown(tokenDecoded.role)) {
      navigateTo("/unauthorized");
      return;
    }
    yield tokenService.saveIdentityTokenResponse(identityTokenResponse);
    const userInfo = {
      username: tokenDecoded?.name || "",
      email: tokenDecoded?.email || "",
      locationId: tokenDecoded.locationId,
      location: tokenDecoded.locationDescription,
      role: tokenDecoded?.role || "",
      acceptLanguage: tokenDecoded?.acceptLanguage,
    };
    yield put(CreatorsUserSession.userSessionUpdateUserData(userInfo));
    yield put(CreatorsUserSession.userSessionSetConciergePermission(userInfo.role));
    setCookiesCurrentPage("lobby");
    saveNotificationToLocalStorage(true);
    yield delay(100);
    navigateTo("/private/lobby");
  } catch (error) {
    const loginErrorMessage = yield getError(error) ?? translate("unable_to_log_in_please_try_again");
    yield YmsNotification({
      type: "error",
      description: loginErrorMessage,
    });
    handleInvalidToken();
    yield delay(500);

    navigateTo("/multiple-realms");
  } finally {
    yield put(Creators.multipleRealmsHideLoading());
  }
}

export function* onSaveUserInfoAsync(action) {
  try {
    yield put(Creators.multipleRealmsShowLoading());
    yield put(Creators.multipleRealmsDisabledRegisterButton());
    const user = {
      location: {
        id: action.locationId,
        description: action.locationDescription,
      },
      name: action.name,
      email: action.email,
      language: action.language,
    };
    yield call(api.saveNewUser, user);

    const registrationSuccessfullyMessage = yield translate("user_successfully_registered");
    yield YmsNotification({
      type: "success",
      description: registrationSuccessfullyMessage,
    });

    navigateTo("/unauthorized");
  } catch (err) {
    console.error(err);
    yield put(Creators.multipleRealmsEnabledRegisterButton());
    const registrationErrorMessage = yield getError(err) ?? translate("cannot_register");
    yield YmsNotification({
      type: "error",
      description: registrationErrorMessage,
    });
  } finally {
    yield put(Creators.multipleRealmsHideLoading());
  }
}

export default function* MultipleRealmsSagas() {
  yield takeLatest(Types.MULTIPLE_REALMS_GET_TOKEN_ASYNC, getTokenAsync);
  yield takeLatest(Types.MULTIPLE_REALMS_SAVE_USER_INFO_ASYNC, onSaveUserInfoAsync);
}
